import { XV2 } from '../../XV2';
import uniq from 'lodash/uniq';
import './MT.scss';
import keyBy from 'lodash/keyBy';
import { createStore$ } from '../../createStore$';
import { useXV2 } from '../../useXV2';
import React from 'react';
import { useOnResume } from '../../hooks/on-resume';
import { useInterval } from 'usehooks-ts';
import { I18n$ } from '../../mod/I18n$';
import { Iot$ } from '../../mod/Iot$';

export namespace Table$ {
    type Screen = 'WELCOME' | 'MENU' | 'CHECKOUT';

    type State = {
        screen: Screen;
        pendingOrder: {
            isPlacingOrder: boolean;
            isBasketVisible: boolean;
            categoryFilter: {
                category?: string;
                subCategory?: string;
            };
        };
        isActiveOrderVisible: boolean;
        animateOrderPlaced: boolean;
    };

    export type Product = XV2.API.V1AppTableBootstrap.Output['products'][0];

    const state$ = createStore$<State>({
        cacheKey: 'Table$',
        initialState: getInitialState(),
    });

    const data$ = createStore$<XV2.API.V1AppTableBootstrap.Output | undefined>({
        initialState: undefined,
    });

    export const useState = state$.useState;
    export const useValue = state$.useValue;

    export const useDataState = data$.useState;

    export function getInitialState(): State {
        return {
            screen: 'WELCOME',
            pendingOrder: getPendingOrderInitialState(),
            isActiveOrderVisible: false,
            animateOrderPlaced: false,
        };
    }

    export function usePromotedProducts() {
        const [state] = data$.useState();
        return (state?.products || []).filter((item) => item.promoted);
    }

    export function useScreen() {
        const [screen, setState] = state$.useState((state) => state.screen);

        return {
            screen,
            navigate(screen: Screen) {
                setState((state) => {
                    state.screen = screen;
                });
            },
        };
    }

    export function useLocationInfo() {
        return data$.useValue((state) => ({
            outletId: state?.outlet.id,
            locationName: state?.location.name,
            logo: state?.location?.imageLogo,
            backgroundImage: state?.location?.imageBackground,
        }));
    }

    export function useMenu() {
        const products = data$.useValue((state) => state?.products || []);
        //const categoryTree: Record<string, Record<string, Set<string>>> = {};

        const categories: Record<
            string,
            {
                rootCategory: string;
                value: string;
                subCategories: string[];
            }
        > = {};

        products.forEach((product) => {
            if (product.rootCategory) {
                //categoryTree[product.rootCategory] = categoryTree[product.rootCategory] || {};

                if (product.category) {
                    categories[product.category] = categories[product.category] || {
                        rootCategory: product.rootCategory,
                        value: product.category,
                        subCategories: [],
                    };

                    //categoryTree[product.rootCategory][product.category] =
                    //    categoryTree[product.rootCategory][product.category] || new Set();

                    if (product.subCategory) {
                        //categoryTree[product.rootCategory][product.category].add(product.subCategory);

                        categories[product.category].subCategories = uniq([
                            ...categories[product.category].subCategories,
                            product.subCategory,
                        ]);
                    }
                }
            }
        });

        return {
            categories: Object.values(categories),
            products: products,
            getProductById(productId: number): Product {
                return keyBy(products, (item) => item.id)[productId];
            },
        };
    }

    function getPendingOrderInitialState(): State['pendingOrder'] {
        return {
            isPlacingOrder: false,
            isBasketVisible: false,
            categoryFilter: {},
        };
    }

    export type UseMenuProductAddInput = {
        variantProductId?: number;
        modGroupIds: Record<number, number[]>;
    };

    export function reset() {
        state$.setValue({ ...getInitialState() });
    }

    export function useQuery() {
        const [lang] = I18n$.useLanguage();
        const [, setState] = data$.useState();

        const query = useXV2(XV2.API.V1AppTableBootstrap, {
            onSuccess(result) {
                setState(() => result);
            },
        });

        return {
            ...query,
            run(input: XV2.API.V1AppTableBootstrap.Input) {
                return query.run({ lang, ...input });
            },
        };
    }

    export function Register({ outletIdOrHash, zoneId }: { outletIdOrHash: string | number; zoneId?: number }) {
        const [lang] = I18n$.useLanguage();
        const query = useQuery();
        const menuId = query.result?.menuId;

        const callback = React.useCallback(() => {
            query.run({
                hash: outletIdOrHash,
                zoneId: zoneId,
                lang: lang,
            });
        }, [outletIdOrHash, zoneId, lang]);

        React.useEffect(() => callback(), [callback]);

        useOnResume(callback, [callback]);

        useInterval(callback, 30 * 1000);

        return <>{menuId && <Iot$.Subscribe topic={`model/Menu/${menuId}/event`} callback={callback} />}</>;
    }
}
